import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { ArrowLeft, ArrowRight, CalendarDays, Clock, Loader2, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ProgressBar } from '@/components/booking/progress-bar';
import { TimePickStep } from '@/components/booking/time-pick-step';
import { QuestionStep } from '@/components/booking/question-step';
import { BookingSuccess } from '@/components/booking/success';
import { phoneProblem } from '@/lib/countries';
import { useTracking } from '@/hooks/use-tracking';
import { usePartialCapture } from '@/hooks/use-partial-capture';
import { usePhoneVerification } from '@/hooks/use-phone-verification';
import { VerifyGate } from '@/components/booking/verify-gate';
import { emit as emitParent } from '@/lib/iframe-events';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Per-session seed pinned in sessionStorage so the scarcity layer
// rotates between visitors but stays stable when the same visitor
// refreshes. Different tabs in the same browser get different seeds.
function getSlotSeed() {
  if (typeof window === 'undefined') return '';
  try {
    const key = 'mm-slot-seed';
    let seed = window.sessionStorage.getItem(key);
    if (!seed) {
      seed = Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.sessionStorage.setItem(key, seed);
    }
    return seed;
  } catch {
    return '';
  }
}

// Multi-step booking flow.
//
// Step 1 is always the date + time picker (with the name/email/phone basics).
// After it come 0..N "questions" steps, built dynamically:
//
//   - New model: each field carries an explicit `step` number (1-based). A
//     questions step exists only if at least one field lives on it, so there
//     are never empty steps and tenants control exactly which page a field
//     appears on. Per-step headline/subtext come from `step_intros` keyed by
//     the step number ({ "1": {headline, subtext}, "2": {...} }).
//   - Legacy fallback (the original MM Performance-Marketer template, whose
//     fields predate `step`): split by the hardcoded EDGE/COMMIT field-ID
//     lists below, with intros under the legacy `edge`/`commit` keys. This
//     keeps un-migrated forms rendering exactly as before.
//
// Field-IDs the legacy template expects (scripts/update_pm_intake_form.sql):
const EDGE_FIELD_IDS = [
  'custom_role',
  'custom_tracking_experience',
  'custom_biggest_challenge',
  'custom_why_now',
  'custom_start_timeline',
  'custom_financial_situation',
  'custom_monthly_income',
];
const COMMIT_FIELD_IDS = ['custom_linkedin_url', 'custom_commitment'];

// Resolve a step's headline/subtext from `step_intros`. Prefers the numeric
// key (new model), falls back to a legacy key (`edge`/`commit`) when given.
function introFor(intros, stepNumber, legacyKey) {
  const src =
    (intros && (intros[String(stepNumber)] || intros[stepNumber])) ||
    (legacyKey && intros && intros[legacyKey]) ||
    {};
  return { headline: src.headline || '', subtext: src.subtext || '' };
}

// Resolve the field IDs into actual field objects from the form schema.
// If the form doesn't have the expected IDs (e.g. a different form), the
// missing slots are simply skipped so simpler forms still render.
function pickFields(allFields, ids) {
  const byId = (allFields || []).reduce((m, f) => { m[f.id] = f; return m; }, {});
  return ids.map((id) => byId[id]).filter(Boolean);
}

// Slice basics out of formData for parent postMessage payloads. Keeps the
// custom_data blob out of step-advanced events that only care about the
// built-in identity fields.
function pickBasics(formData) {
  const { first_name, last_name, email, phone, company, website } = formData || {};
  return { first_name, last_name, email, phone, company, website };
}

// Grab only the custom_data answers for a given list of field IDs.
function pickByIds(customData, ids) {
  if (!customData) return {};
  const out = {};
  for (const id of ids) {
    if (customData[id] !== undefined) out[id] = customData[id];
  }
  return out;
}

// Shared format rules - kept in sync with the server (api/src/routes/leads.js).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function isValidEmail(e) { return EMAIL_RE.test(String(e || '').trim()); }
// Validate name/email/phone basics. Returns an error string, or null.
function validateBasics(formData) {
  if (!formData.first_name?.trim()) return 'First name is required.';
  if (!formData.email?.trim()) return 'Email is required.';
  if (!isValidEmail(formData.email)) return 'Please enter a valid email address.';
  if (!formData.phone?.trim()) return 'Phone number is required.';
  // Judged per country (the picker fixes the code, this fixes the digit count).
  const p = phoneProblem(formData.phone);
  if (p) return p;
  return null;
}

// Validation per step kind. Returns an error string, or null when valid.
function validateStep(step, formData, selectedDate, selectedSlot) {
  if (step.kind === 'timepick') {
    if (!selectedDate) return 'Please pick a date.';
    if (!selectedSlot) return 'Please pick a time.';
    // Basics live on this step too once a time is picked.
    return validateBasics(formData);
  }
  // Every other step is a questions page - validate its own fields.
  return validateRequiredCustomFields(step.fields, formData);
}

function validateRequiredCustomFields(fields, formData) {
  for (const f of fields || []) {
    if (!f.required) continue;
    const v = formData.custom_data?.[f.id];
    if (f.type === 'checkbox') {
      if (!Array.isArray(v) || v.length === 0) return `"${f.name}": pick at least one option.`;
    } else if (!v || (typeof v === 'string' && !v.trim())) {
      return `"${f.name}" is required.`;
    }
  }
  return null;
}

export default function LeadForm({ userId, formConfig }) {
  const tracking = useTracking();

  // Step machine: always the time-picker, then a questions page per distinct
  // `step` number that actually has fields (so empty steps can't exist).
  const steps = useMemo(() => {
    const intros = formConfig?.step_intros || {};
    const allFields = Array.isArray(formConfig?.fields) ? formConfig.fields : [];
    // New model when any field declares an explicit `step`.
    const explicit = allFields.some((f) => Number(f?.step) >= 1);

    let questionSteps;
    if (explicit) {
      const byStep = new Map();
      for (const f of allFields) {
        const n = Number(f?.step) >= 1 ? Math.floor(Number(f.step)) : 1;
        if (!byStep.has(n)) byStep.set(n, []);
        byStep.get(n).push(f);
      }
      questionSteps = [...byStep.keys()]
        .sort((a, b) => a - b)
        .map((n, i) => ({
          kind: 'question',
          label: i === 0 ? 'Your details' : 'More details',
          fields: byStep.get(n),
          ...introFor(intros, n),
        }));
    } else {
      // Legacy MM template: split by hardcoded edge/commit field ids.
      let edgeFields = pickFields(allFields, EDGE_FIELD_IDS);
      const commitFields = pickFields(allFields, COMMIT_FIELD_IDS);
      if (edgeFields.length === 0 && commitFields.length === 0) edgeFields = allFields;
      questionSteps = [
        { kind: 'question', label: 'Your details', fields: edgeFields, ...introFor(intros, 1, 'edge') },
        { kind: 'question', label: 'Almost done', fields: commitFields, ...introFor(intros, 2, 'commit') },
      ].filter((s) => (s.fields && s.fields.length) || s.headline || s.subtext);
    }

    return [{ kind: 'timepick', label: 'Pick a time' }, ...questionSteps];
  }, [formConfig]);

  const [stepIdx, setStepIdx] = useState(0);
  const currentStep = steps[stepIdx];
  const isLast = stepIdx === steps.length - 1;

  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    custom_data: {},
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  // Cold-capture: quietly record started-but-not-submitted entries (opt-in per
  // form). Checkpoint-based - captured on each step advance (see goNext) plus a
  // tab-hide beacon. Retired automatically if this visitor becomes a real lead.
  const { capture: capturePartial } = usePartialCapture({ formConfig, formData, tracking, submitted });

  // A booking form has the same problem as a lead form and a worse cost: a fake
  // number is a slot held, a rep's hour blocked, and a no-show.
  const verification = usePhoneVerification({ formConfig, phone: formData.phone });
  // True between "they pressed the button" and "the code came back right".
  const [pendingSubmit, setPendingSubmit] = useState(false);

  // Stable seed for this visitor session; the server uses it to pick
  // which slots stay available, so two different visitors see two
  // different schedules.
  const slotSeed = useMemo(() => getSlotSeed(), []);

  // ---- Fetch availability --------------------------------------------------
  // `background` refreshes update the slot list silently (no spinner / no
  // error banner) so they don't flicker the UI mid-browse.
  const loadSlots = useCallback((background = false) => {
    if (!formConfig?.id && !userId) return;
    if (!background) setSlotsLoading(true);
    // Prefer the form-aware endpoint: it resolves the booking window + handles
    // both single-rep and pooled (round-robin) forms server-side. Fall back to
    // the per-rep endpoint only for legacy ad-hoc links that pass just a userId.
    const url = formConfig?.id
      ? `${API_URL}/api/availability/slots-for-form/${formConfig.id}`
      : `${API_URL}/api/availability/slots/${userId}`;
    axios
      .get(url, { params: { seed: slotSeed } })
      .then((res) => setSlots(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error('Slots fetch failed:', err);
        if (!background) setError('Could not load available times. Please refresh.');
      })
      .finally(() => { if (!background) setSlotsLoading(false); });
  }, [userId, slotSeed, formConfig?.id]);

  // Initial load (with spinner).
  useEffect(() => { loadSlots(false); }, [loadSlots]);

  // Keep availability fresh while the visitor is still choosing a time: poll
  // every 45s and refetch the moment the tab regains focus. This way a slot
  // freed by a reschedule or cancellation shows up within seconds instead of
  // only on a manual reload. We stop once a slot is picked so the visitor's
  // chosen time can't get pulled out from under them, and pause when the tab
  // is hidden to avoid pointless requests.
  useEffect(() => {
    if (submitted || selectedSlot || (!userId && !formConfig?.id)) return;
    const REFRESH_MS = 45_000;
    const tick = () => { if (document.visibilityState === 'visible') loadSlots(true); };
    const id = setInterval(tick, REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') loadSlots(true); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [submitted, selectedSlot, userId, formConfig?.id, loadSlots]);

  // ---- Tell the parent page (academy.measuremarketer.com etc.) about
  // ---- view + every state change so they can push to GTM dataLayer.
  useEffect(() => {
    if (!formConfig?.id) return;
    emitParent('mm:form:view', { formId: formConfig.id, formName: formConfig.name });
  }, [formConfig?.id, formConfig?.name]);

  // ---- Broadcast our rendered height so the parent can resize the iframe.
  // iOS WebKit sizes iframes to their content regardless of the height
  // attribute, so a fixed-height parent box makes our overflow invisibly
  // cover whatever sits below the embed and swallow taps (steps 2/3 of this
  // form are ~1200px on a 375px viewport, taller than the classic 1000px
  // embed). Parents listen for mm:form:height and keep the box in sync.
  useEffect(() => {
    let last = 0;
    const measure = () => {
      const h = Math.max(
        document.documentElement.scrollHeight,
        document.body ? document.body.scrollHeight : 0
      );
      if (Math.abs(h - last) > 4) {
        last = h;
        emitParent('mm:form:height', { height: h });
      }
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && document.body) ro.observe(document.body);
    window.addEventListener('load', measure);
    const id = setInterval(measure, 1200); // safety net for async content/fonts
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('load', measure);
      clearInterval(id);
    };
  }, []);

  // ---- Group slots by date for the calendar --------------------------------
  // A date counts as "open" only if it has at least one status='available'
  // slot. Days where every slot is booked drop out of the date picker.
  const { availableDates, slotsByDate } = useMemo(() => {
    const byDate = {};
    for (const slot of slots) {
      const day = format(parseISO(slot.time), 'yyyy-MM-dd');
      if (!byDate[day]) byDate[day] = [];
      byDate[day].push(slot);
    }
    const open = Object.keys(byDate)
      .filter((day) => byDate[day].some((s) => s.status === 'available'))
      .sort();
    return { availableDates: open, slotsByDate: byDate };
  }, [slots]);

  // Real scarcity: how many bookable openings remain in the next 7 days. Counted
  // from genuine availability (the form already only surfaces a few slots/day),
  // so "Only N left this week" is honest, not invented.
  const slotsLeftThisWeek = useMemo(() => {
    if (!formConfig?.show_slots_left) return 0;
    const now = Date.now();
    const weekEnd = now + 7 * 86_400_000;
    return slots.filter((s) => {
      if (s.status !== 'available') return false;
      const t = new Date(s.time).getTime();
      return t >= now && t <= weekEnd;
    }).length;
  }, [slots, formConfig?.show_slots_left]);

  // ---- Navigation -----------------------------------------------------------
  function goNext() {
    setError('');
    const err = validateStep(currentStep, formData, selectedDate, selectedSlot);
    if (err) return setError(err);

    // Emit parent event with the slice of data this step captured. Lets the
    // host page track funnel drop-off at each stage (basics filled, edge
    // answered, etc.).
    const stepPayload = {
      formId: formConfig?.id,
      fromStep: currentStep.kind,
      toStep: steps[Math.min(steps.length - 1, stepIdx + 1)]?.kind,
    };
    if (currentStep.kind === 'timepick') {
      stepPayload.basics = pickBasics(formData);
    } else if (currentStep.fields?.length) {
      const ids = currentStep.fields.map((f) => f.id);
      stepPayload.answers = pickByIds(formData.custom_data, ids);
      // Back-compat: the academy GTM listens for `edge` on the questions step.
      const edgeAnswers = pickByIds(formData.custom_data, EDGE_FIELD_IDS);
      if (Object.keys(edgeAnswers).length) stepPayload.edge = edgeAnswers;
    }
    emitParent('mm:step:advanced', stepPayload);

    // Checkpoint: leaving a step with contact info (step 1 holds name/email/phone)
    // records the cold capture. Deduped, so later advances with no new data are free.
    capturePartial();

    setStepIdx((i) => Math.min(steps.length - 1, i + 1));
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function goBack() {
    setError('');
    setStepIdx((i) => Math.max(0, i - 1));
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ---- Submit (only fires on last step) ------------------------------------
  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const err = validateStep(currentStep, formData, selectedDate, selectedSlot);
    if (err) return setError(err);
    // Re-check the basics at submit time too (defense against editing a field
    // on a later step), so a bad email/phone can never reach the API.
    const basicsErr = validateBasics(formData);
    if (basicsErr) return setError(basicsErr);
    if (!selectedSlot) return setError('Please select a time slot.');
    if (formConfig?.confirm_text && !confirmed) {
      return setError('Please tick the confirmation box before booking.');
    }

    // Everything else checked out, so now - and only now - ask for the code.
    // Sending it before the form is valid would text someone a code and then
    // refuse their booking for a missing answer.
    if (verification.active && verification.required && !verification.verified) {
      setPendingSubmit(true);
      if (verification.status === 'idle') await verification.send();
      return; // the panel is open; entering the code finishes the booking
    }

    await doSubmit();
  }

  // Resume the moment the number is proven. The visitor pressed the button
  // once and meant it - making them press it again after typing the code is
  // the dead end this whole change exists to remove.
  useEffect(() => {
    if (pendingSubmit && verification.verified) { setPendingSubmit(false); doSubmit(); }
  }, [pendingSubmit, verification.verified]); // eslint-disable-line react-hooks/exhaustive-deps

  async function doSubmit() {
    setError('');
    setSubmitting(true);
    try {
      const cookiesData = tracking.withEventMeta();

      const leadRes = await axios.post(`${API_URL}/api/leads`, {
        ...formData,
        cookies_data: cookiesData,
        source: cookiesData.utm_source || window.location.hostname,
        // Same rep the meeting will book against - keeps lead.assigned_to
        // and meetings.assigned_user_id in sync from the start.
        assigned_to: userId,
        // Which form produced this lead — drives per-form default routing
        // (pipeline + stage) and lets workflow conditions key off the form.
        form_id: formConfig?.id,
        ...(verification.token ? { verification_token: verification.token } : {}),
      });
      const leadId = leadRes.data.id;

      await axios.post(`${API_URL}/api/meetings`, {
        lead_id: leadId,
        assigned_user_id: userId,
        scheduled_time: selectedSlot.time,
        seed: slotSeed,
        form_id: formConfig?.id,
      });

      // Final parent event: full payload (basics + all custom answers) so
      // GTM dataLayer can fire whatever conversion tags the academy site
      // needs (Meta Lead, GA4 generate_lead, Google Ads conversion, etc.).
      emitParent('mm:lead:submitted', {
        formId: formConfig?.id,
        leadId,
        scheduledTime: selectedSlot.time,
        fields: {
          ...pickBasics(formData),
          ...formData.custom_data,
        },
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Submit failed:', err);
      const message = err.response?.data?.error || 'Something went wrong. Please try again.';
      setError(message);
      emitParent('mm:lead:error', { formId: formConfig?.id, error: message });
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Success state --------------------------------------------------------
  if (submitted) {
    return (
      <Card className="mx-auto w-full max-w-xl shadow-elevated">
        <CardContent className="p-8">
          <BookingSuccess selectedSlot={selectedSlot} formData={formData} />
        </CardContent>
      </Card>
    );
  }

  // ---- Step body -----------------------------------------------------------
  const renderStep = () => {
    if (currentStep.kind === 'timepick') {
      return (
        <TimePickStep
          slotsLoading={slotsLoading}
          availableDates={availableDates}
          slotsByDate={slotsByDate}
          selectedDate={selectedDate}
          selectedSlot={selectedSlot}
          onDateSelect={(d) => {
            setSelectedDate(d);
            setSelectedSlot(null);
            // Pill-bar "change" sends null; only emit on real picks.
            if (d) emitParent('mm:date:selected', { formId: formConfig?.id, date: d });
          }}
          onSlotSelect={(slot) => {
            setSelectedSlot(slot);
            if (slot) {
              emitParent('mm:time:selected', {
                formId: formConfig?.id,
                date: selectedDate,
                time: slot.time,
              });
            }
          }}
          formData={formData}
          onFormDataChange={setFormData}
          formConfig={formConfig}
          onBasicsBlur={capturePartial}
        />
      );
    }
    // edge + commit both render the same QuestionStep with the tenant's copy.
    return (
      <QuestionStep
        headline={currentStep.headline}
        subtext={currentStep.subtext}
        fields={currentStep.fields}
        formData={formData}
        onChange={setFormData}
      />
    );
  };

  // Step-level heading shown only on the date + time picker. Questions steps
  // carry their own headline/subtext inside renderStep.
  const stepHeading =
    currentStep.kind === 'timepick' ? 'Pick a time that works for you' : null;

  return (
    <Card className="mx-auto w-full max-w-2xl shadow-elevated">
      <CardHeader className="border-b">
        <CardTitle className="text-xl">
          {formConfig?.name || 'Book a discovery call'}
        </CardTitle>
        {formConfig?.description ? (
          <CardDescription>{formConfig.description}</CardDescription>
        ) : (
          <CardDescription>
            30-minute call with a Measure Marketer specialist. Google Meet.
          </CardDescription>
        )}
        <div className="mt-3">
          <ProgressBar
            current={stepIdx + 1}
            total={steps.length}
            label={currentStep.label}
          />
        </div>
      </CardHeader>

      {formConfig?.proof_text && (
        <div className="flex items-center justify-center gap-1.5 border-b border-border bg-primary-soft/40 px-6 py-2.5 text-center text-sm font-medium text-primary">
          <Sparkles className="size-3.5 shrink-0" />
          <span>{formConfig.proof_text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4 pt-5">
          {stepHeading && (
            <div className="flex items-center gap-2">
              <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary">
                <CalendarDays className="size-4" />
              </div>
              <h3 className="text-base font-semibold tracking-tight">{stepHeading}</h3>
            </div>
          )}

          {currentStep.kind === 'timepick' && formConfig?.show_slots_left && slotsLeftThisWeek > 0 && (
            <p className="flex items-center gap-1.5 text-xs font-semibold text-primary">
              <Clock className="size-3.5 shrink-0" />
              Only {slotsLeftThisWeek} slot{slotsLeftThisWeek === 1 ? '' : 's'} left this week - grab one before they go.
            </p>
          )}

          {renderStep()}

          {isLast && formConfig?.confirm_text && (
            <label className="flex items-start gap-2 rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-0.5 size-4 shrink-0 accent-primary"
              />
              <span>{formConfig.confirm_text}</span>
            </label>
          )}

          {error && (
            <div role="alert" className="rounded-md border border-destructive-soft bg-destructive-soft/60 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          {isLast && verification.active && (
            <VerifyGate
              v={verification}
              phone={formData.phone}
              onPhoneChange={(e164) => setFormData((d) => ({ ...d, phone: e164 }))}
              busy={submitting}
            />
          )}
        </CardContent>

        <div className="space-y-2 border-t border-border bg-muted/30 px-6 py-4">
          <div className="flex items-center justify-between">
            {stepIdx > 0 ? (
              <Button type="button" variant="ghost" size="sm" onClick={goBack} disabled={submitting}>
                <ArrowLeft className="size-4" />
                Back
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                {availableDates.length > 0 && `${availableDates.length} dates open`}
              </span>
            )}

            {!isLast ? (
              <Button type="button" onClick={goNext}>
                Continue
                <ArrowRight className="size-4" />
              </Button>
            ) : (
              <Button type="submit" disabled={submitting || (!!formConfig?.confirm_text && !confirmed)} size="lg">
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Booking&hellip;
                  </>
                ) : verification.active && !verification.verified ? (
                  // The label states the extra step BEFORE it is asked for, so
                  // the code arriving is never a surprise.
                  <>Verify &amp; {(formConfig?.submit_label || 'Book my call').toLowerCase()}</>
                ) : (
                  <>{formConfig?.submit_label || 'Book my call'}</>
                )}
              </Button>
            )}
          </div>
          {isLast && formConfig?.cta_note && (
            <p className="text-right text-xs text-muted-foreground">{formConfig.cta_note}</p>
          )}
          {isLast && formConfig?.privacy_note && (
            <p className="text-right text-[11px] text-muted-foreground/80">{formConfig.privacy_note}</p>
          )}
        </div>
      </form>
    </Card>
  );
}
